namespace $ {

	export type $shm_hitalama_method_decorator = 'mem' | 'mem_key' | 'process_mem' | 'view' | 'view_key' | 'action' | 'process_action' | 'none'

	export type $shm_hitalama_method_def = {
		name: string
		code: string
		decorator?: $shm_hitalama_method_decorator | string
		args?: string
		source?: any
	}

	export function $shm_hitalama_method_decorator( value: any ): $shm_hitalama_method_decorator {
		switch( value ) {
			case 'mem':
			case 'mem_key':
			case 'process_mem':
			case 'view':
			case 'view_key':
			case 'action':
			case 'process_action':
			case 'none':
				return value
			default:
				return 'mem'
		}
	}

	export function $shm_hitalama_method_define(
		$: $,
		host: object,
		name: string,
		decorator: $shm_hitalama_method_decorator,
		handler: Function,
	) {
		Reflect.defineProperty( handler, 'name', { value: name } )

		Object.defineProperty( host, name, {
			value: handler,
			writable: true,
			configurable: true,
		} )

		const descr = Object.getOwnPropertyDescriptor( host, name )
		if( !descr ) return

		switch( decorator ) {
			case 'mem':
			case 'view':
				$.$mol_mem( host, name, descr )
				break
			case 'mem_key':
			case 'view_key':
				$.$mol_mem_key( host, name, descr )
				break
			case 'action':
				$.$mol_action( host, name, descr )
				break
			case 'process_mem':
				$.$shm_hitalama_process_mem( host, name, descr )
				break
			case 'process_action':
				$.$shm_hitalama_process_wire_action( host, name, descr )
				break
		}
	}

	export class $shm_hitalama_method_app extends $mol_object {

		[ name: string ]: any

	}

	export class $shm_hitalama_method_runtime extends $mol_object {

		reserved_names() {
			return new Set([
				'constructor',
				'__proto__',
				'prototype',
			])
		}

		methods(): readonly $shm_hitalama_method_def[] {
			return []
		}

		method_name_valid( name: string ) {
			return /^[A-Za-z_$][\w$]*$/.test( name )
		}

		method_name( method: $shm_hitalama_method_def ) {
			const name = method.name
			if( !this.method_name_valid( name ) ) return null
			if( this.reserved_names().has( name ) ) return null
			return name
		}

		method_decorator( method: $shm_hitalama_method_def ) {
			return this.$.$shm_hitalama_method_decorator( method.decorator )
		}

		method_args( method: $shm_hitalama_method_def ) {
			const args = method.args ?? this.method_args_default( this.method_decorator( method ) )
			return String( args )
				.split( ',' )
				.map( arg => arg.trim() )
				.filter( Boolean )
		}

		method_args_default( decorator: $shm_hitalama_method_decorator ) {
			switch( decorator ) {
				case 'mem_key':
					return 'key, next'
				case 'process_mem':
					return 'next'
				case 'view':
					return ''
				case 'view_key':
					return 'key'
				case 'action':
				case 'process_action':
				case 'none':
					return 'arg'
				default:
					return 'next'
			}
		}

		@ $mol_mem_key
		value( key: string, next?: any ) {
			return next
		}

		@ $mol_mem
		app() {
			return this.app_make( this.methods() )
		}

		app_make( methods: readonly $shm_hitalama_method_def[] ) {
			const app = new this.$.$shm_hitalama_method_app

			this.method_define(
				app,
				'value',
				'mem_key',
				( key: string, next?: any )=> this.value( key, next ),
			)

			for( const method of methods ) {
				const name = this.method_name( method )
				if( !name ) continue
				const runtime = this
				this.method_define(
					app,
					name,
					this.method_decorator( method ),
					( ... args: any[] )=> {
						return runtime.method_run( method, app, args )
					},
				)
			}

			return app
		}

		@ $mol_mem
		source() {
			return this.$.$shm_hitalama_method_source.make({
				runtime: ()=> this,
			})
		}

		method_define(
			app: $shm_hitalama_method_app,
			name: string,
			decorator: $shm_hitalama_method_decorator,
			handler: Function,
		) {
			this.$.$shm_hitalama_method_define( this.$, app, name, decorator, handler )
		}

		method_run( method: $shm_hitalama_method_def, app: $shm_hitalama_method_app, values: any[] ) {
			this.$.$shm_hitalama_compat_fqn_install( this.$ as any )
			const func = new Function( '$', 'method', ... this.method_args( method ), method.code )
			return func.call( app, this.$, method, ... values )
		}

		method_import_start( methods: readonly $shm_hitalama_method_def[] ) {
		}

		method_import_end() {
		}

		method_save( method: $shm_hitalama_method_def, index = 0 ) {
		}

		method_name_escape( name: string ) {
			return name.replace( /[\\^$.*+?()[\]{}|]/g, '\\$&' )
		}

		@ $mol_action
		method_rename( from: string, to: string ) {
			if( from === to ) return
			if( !this.method_name_valid( from ) ) return
			if( !this.method_name_valid( to ) ) return

			const escaped = this.method_name_escape( from )
			const search = new RegExp( `\\bthis\\s*\\.\\s*${ escaped }\\s*\\(`, 'g' )

			for( const method of this.methods() ) {
				const code = method.code
				const next = code.replace( search, `this.${ to }(` )
				if( next === code ) continue
				this.method_save({ ... method, code: next })
			}
		}

	}

}
